import { Reveal, Eyebrow, PageHero, ArrowButton, ChipFlow } from "@/components/bits";
import { EVOLUTION, PHILOSOPHY_FLOW } from "@/data/content";

const ABOUT_CAPABILITIES = [
  { title: "Clinical Nutrition", desc: "Clinical nutrition expertise and evidence-based care models." },
  { title: "Digital Health", desc: "Digital products, patient experiences and technology-enabled care." },
  { title: "Product Development", desc: "Discovery, product strategy, clinical workflows, UX and implementation." },
  { title: "Healthcare Innovation", desc: "New models of care, service design and healthcare transformation." },
  { title: "Education", desc: "Nutrition education, culinary nutrition and capacity building." },
  { title: "Strategic Partnerships", desc: "Working with organisations to develop and implement solutions." },
];

const ABOUT_FAQS = [
  {
    q: "What is The Nutrition Network Africa (TNN Africa)?",
    a: "The Nutrition Network Africa (TNN Africa) is a nutrition and health innovation organisation founded in 2021, working at the intersection of clinical nutrition, digital health, product development, education and strategic partnerships across Africa and Europe.",
  },
  {
    q: "What does TNN Africa do?",
    a: "TNN Africa builds and supports nutrition and healthcare solutions: digital health products, nutrition technology, clinical programmes, education initiatives and venture building with founders and healthcare organisations.",
  },
  {
    q: "Who founded TNN Africa?",
    a: "TNN Africa was founded in 2021 by Tito Ipinmoye, a healthcare innovation executive, product leader and clinical dietitian, and Evi Uwede.",
  },
  {
    q: "What has TNN Africa built?",
    a: "TNN Africa's portfolio includes PlateMind (AI-powered nutrition and diabetes decision support), OncoNourish (oncology nutrition support), Lana Clinical (AI-enabled healthcare), Sito Health (metabolic health care) and The Balance Clinic, alongside partner ventures such as Akaani, FitPrep, PocketFood, Qilo and ChroniCare, and the Healthy Cooking Masterclass education initiative.",
  },
  {
    q: "Who does TNN Africa work with?",
    a: "Healthcare organisations, founders and startups, technology companies, universities, research and grant partners, and public-health organisations. Partnership enquiries can be made through the contact page.",
  },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: ABOUT_FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function About() {
  return (
    <main data-testid="about-page">
      <PageHero
        testid="about-hero"
        eyebrow="About TNN Africa"
        title={
          <>
            Building at the intersection of <span className="text-tnn-purple">nutrition, healthcare</span> and technology.
          </>
        }
        lead="The Nutrition Network Africa is a nutrition and health innovation organisation working to improve how people access, experience and benefit from nutrition and healthcare. Founded in 2021 by Tito Ipinmoye and Evi Uwede, the organisation has grown from clinical nutrition practice into a broader health innovation practice."
      />

      <section data-testid="about-story" className="bg-white py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Our Story</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-tnn-ink max-w-2xl leading-[1.15]">
              An evolution shaped by the problems we set out to solve.
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {EVOLUTION.map((stage, i) => (
              <Reveal key={stage.title} delay={i * 0.06}>
                <div
                  data-testid={`story-chapter-${i}`}
                  className="bg-tnn-warm border border-tnn-ink/6 rounded-3xl p-7 h-full transition-colors duration-300 hover:border-tnn-purple/30"
                >
                  <span className="w-8 h-8 rounded-full bg-tnn-soft text-tnn-purple text-xs font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <h3 className="mt-5 font-display text-lg font-semibold text-tnn-ink">{stage.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-tnn-muted">{stage.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="about-capabilities" className="bg-tnn-warm py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            <div className="lg:col-span-2">
              <div className="lg:sticky lg:top-28">
                <Reveal>
                  <Eyebrow>Our Capabilities</Eyebrow>
                  <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-tnn-ink leading-[1.15]">
                    Six disciplines. One practice.
                  </h2>
                  <p className="mt-5 text-[15px] text-tnn-muted leading-relaxed">
                    We combine clinical depth with the ability to design, build and implement, across products,
                    programmes and systems.
                  </p>
                </Reveal>
              </div>
            </div>
            <div className="lg:col-span-3 border-t border-tnn-ink/8">
              {ABOUT_CAPABILITIES.map((cap, i) => (
                <Reveal key={cap.title} delay={i * 0.04}>
                  <div
                    data-testid={`about-capability-${i}`}
                    className="group py-6 border-b border-tnn-ink/8"
                  >
                    <div className="flex items-baseline gap-4">
                      <span className="text-xs font-bold text-tnn-purple/50">0{i + 1}</span>
                      <h3 className="font-display text-xl font-semibold text-tnn-ink group-hover:text-tnn-purple transition-colors duration-200">
                        {cap.title}
                      </h3>
                    </div>
                    <p className="mt-2 ml-9 text-sm text-tnn-muted leading-relaxed">{cap.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section data-testid="about-philosophy" className="bg-white py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Our Philosophy</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-tnn-ink max-w-2xl leading-[1.15]">
              Clinical first. Technology enabled. Human centred.
            </h2>
            <p className="mt-5 text-[15px] text-tnn-muted leading-relaxed max-w-xl">
              We believe technology should begin with the problem, not the technology.
            </p>
          </Reveal>
          <Reveal delay={0.12} className="mt-10">
            <ChipFlow items={PHILOSOPHY_FLOW} separator="→" />
          </Reveal>
          <Reveal delay={0.2} className="mt-12">
            <ArrowButton to="/contact" testid="philosophy-cta">Partner With TNN Africa</ArrowButton>
          </Reveal>
        </div>
      </section>

      <section data-testid="about-faq" className="bg-tnn-warm py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Common Questions</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-tnn-ink max-w-2xl leading-[1.15]">
              Quick answers about TNN Africa.
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-5">
            {ABOUT_FAQS.map((f, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div
                  data-testid={`faq-${i}`}
                  className="bg-white border border-tnn-ink/6 rounded-3xl p-7 h-full transition-colors duration-300 hover:border-tnn-purple/30"
                >
                  <h3 className="font-display text-lg font-semibold text-tnn-ink leading-snug">{f.q}</h3>
                  <p className="mt-3 text-sm text-tnn-muted leading-relaxed">{f.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      </section>
    </main>
  );
}
